import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Loader2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { toast } from 'sonner';

// TREATX-TAX-3 / TAX-4 — Super Admin surface for the VAT TreatX charges on
// its OWN commission supply. Lives inside the existing Commission Rules
// screen (same authority, same finance permissions) — no duplicate admin
// menu, no second tax module. Saving is NOT activating.
const TREATMENTS = ['STANDARD', 'EXEMPT', 'REVERSE_CHARGE', 'OUT_OF_SCOPE'];

const EMPTY = {
  jurisdiction: '',
  selling_legal_entity_id: '',
  tax_treatment: 'STANDARD',
  tax_rate_bps: '',
  legal_basis: '',
  approval_reference: '',
};

export const CommissionVatPanel = () => {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [meta, setMeta] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/commission/vat-policies');
      setPolicies(data?.policies || []);
      setMeta({
        enabled: !!data?.commission_vat_enabled,
        productionGate: !!data?.production_gate_active,
      });
    } catch { /* surfaced by the empty state */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/commission/vat-policies', {
        jurisdiction: form.jurisdiction.trim().toUpperCase(),
        selling_legal_entity_id: form.selling_legal_entity_id.trim() || null,
        tax_treatment: form.tax_treatment,
        tax_rate_bps: parseInt(form.tax_rate_bps, 10),
        legal_basis: form.legal_basis.trim() || null,
        approval_reference: form.approval_reference.trim() || null,
      });
      toast.success(t('admin.commissionVat.saved'));
      setForm(EMPTY);
      await fetchPolicies();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Save failed');
    }
    setSaving(false);
  };

  const activate = async (policyId) => {
    try {
      await api.post(`/commission/vat-policies/${policyId}/activate`);
      toast.success(t('admin.commissionVat.activated'));
      await fetchPolicies();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Activation failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="commission-vat-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('admin.commissionVat.title')}</CardTitle>
          <p className="text-sm text-slate-500">{t('admin.commissionVat.subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!meta.enabled && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="commission-vat-flag-off">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{t('admin.commissionVat.flagOffWarning')}</span>
            </div>
          )}
          <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="commission-vat-international-note">
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{t('admin.commissionVat.internationalNote')}</span>
          </div>

          {/* Unsaved, unapproved domestic suggestion — fills the form only. */}
          <div className="rounded-md border border-dashed border-slate-300 px-3 py-2" data-testid="commission-vat-tr-suggestion">
            <p className="text-xs font-semibold text-slate-700">{t('admin.commissionVat.suggestionTitle')}</p>
            <p className="text-xs text-slate-500">{t('admin.commissionVat.suggestionBody')}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="commission-vat-use-suggestion-btn"
              onClick={() => setForm({ ...EMPTY, jurisdiction: 'TR', tax_treatment: 'STANDARD', tax_rate_bps: '2000' })}
            >
              {t('admin.commissionVat.useSuggestion')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('admin.commissionVat.jurisdiction')} *</Label>
              <Input
                value={form.jurisdiction}
                onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                placeholder="TR"
                data-testid="commission-vat-jurisdiction"
              />
            </div>
            <div>
              <Label className="text-xs">{t('admin.commissionVat.legalEntity')}</Label>
              <Input
                value={form.selling_legal_entity_id}
                onChange={(e) => setForm({ ...form, selling_legal_entity_id: e.target.value })}
                placeholder={t('admin.commissionVat.legalEntityAll')}
                data-testid="commission-vat-legal-entity"
              />
            </div>
            <div>
              <Label className="text-xs">{t('admin.commissionVat.taxTreatment')} *</Label>
              <select
                className="w-full p-2 border rounded-md text-sm"
                value={form.tax_treatment}
                onChange={(e) => setForm({ ...form, tax_treatment: e.target.value })}
                data-testid="commission-vat-treatment"
              >
                {TREATMENTS.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">{t('admin.commissionVat.rate')} *</Label>
              <Input
                type="number"
                value={form.tax_rate_bps}
                onChange={(e) => setForm({ ...form, tax_rate_bps: e.target.value })}
                placeholder="2000"
                data-testid="commission-vat-rate"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">{t('admin.commissionVat.legalBasis')}</Label>
              <Input
                value={form.legal_basis}
                onChange={(e) => setForm({ ...form, legal_basis: e.target.value })}
                data-testid="commission-vat-legal-basis"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">{t('admin.commissionVat.approvalReference')}</Label>
              <Input
                value={form.approval_reference}
                onChange={(e) => setForm({ ...form, approval_reference: e.target.value })}
                data-testid="commission-vat-approval-ref"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving || !form.jurisdiction || form.tax_rate_bps === ''} data-testid="commission-vat-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('admin.commissionVat.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" />
          ) : policies.length === 0 ? (
            <p className="text-sm text-slate-500 text-center" data-testid="commission-vat-empty">
              {t('admin.commissionVat.none')}
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="commission-vat-table">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left font-medium">{t('admin.commissionVat.jurisdiction')}</th>
                  <th className="text-left font-medium">{t('admin.commissionVat.taxTreatment')}</th>
                  <th className="text-right font-medium">{t('admin.commissionVat.rate')}</th>
                  <th className="text-left font-medium pl-3">{t('admin.commissionVat.status')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.policy_id} className="border-t" data-testid={`commission-vat-row-${p.jurisdiction}`}>
                    <td className="py-2">{p.jurisdiction}{p.selling_legal_entity_id ? ` · ${p.selling_legal_entity_id}` : ''}</td>
                    <td className="py-2">{p.tax_treatment}</td>
                    <td className="py-2 text-right">{p.tax_rate_bps}</td>
                    <td className="py-2 pl-3">
                      <Badge variant={p.status === 'ACTIVE' ? 'default' : 'secondary'}>
                        {p.status === 'ACTIVE' ? t('admin.commissionVat.statusActive') : t('admin.commissionVat.statusDraft')}
                      </Badge>
                    </td>
                    <td className="py-2 text-right">
                      {p.status !== 'ACTIVE' && (
                        <Button size="sm" variant="outline" onClick={() => activate(p.policy_id)} data-testid={`commission-vat-activate-${p.jurisdiction}`}>
                          <Check className="h-3.5 w-3.5 mr-1" />{t('admin.commissionVat.activate')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommissionVatPanel;
